import { CalendarEventCard } from "@/components/event/CalendarEventCard.tsx";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { InformBox } from "@/components/inform-box/InformBox.tsx";
import {
  getBusyFilter,
  getGmtLabel,
  getTimelineBlock,
  suggestAvailableTimes,
} from "@/components/event/event-availability.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useEmployeesBusy } from "@/queries/use-events.query.ts";
import { ApiEventAttendee } from "@/types/event.types.ts";
import {
  Button,
  ButtonIcon,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@skailer-com/uikit";
import {
  addDays,
  addMinutes,
  differenceInMinutes,
  format,
  set,
  startOfDay,
  startOfWeek,
} from "date-fns";
import { enGB, ru } from "date-fns/locale";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface EventAvailabilityPanelProps {
  attendees: ApiEventAttendee[];
  start: Date;
  end: Date;
  timezone: string;
  isAllDay: boolean;
  isEditing: boolean;
  hasGroups: boolean;
  onSelect: (start: Date, end: Date) => void;
  onClose: () => void;
}

const hourHeight = 56;
const hours = Array.from({ length: 24 }, (_, hour) => hour);

export function EventAvailabilityPanel({
  attendees,
  start,
  end,
  timezone,
  isAllDay,
  isEditing,
  hasGroups,
  onSelect,
  onClose,
}: EventAvailabilityPanelProps) {
  const { t, i18n } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const locale = i18n.resolvedLanguage?.startsWith("ru") ? ru : enGB;
  const [mode, setMode] = useState<"day" | "week">("day");
  const [viewDate, setViewDate] = useState(startOfDay(start));
  const startTimestamp = start.getTime();
  const selectionKey = `${startTimestamp}:${timezone}`;
  const [syncedSelection, setSyncedSelection] = useState(selectionKey);
  if (syncedSelection !== selectionKey) {
    setSyncedSelection(selectionKey);
    setViewDate(startOfDay(start));
  }
  const scrollRef = useRef<HTMLDivElement>(null);
  const employees = [
    ...new Map(
      attendees
        .flatMap((attendee) => (attendee.employee ? [attendee.employee] : []))
        .map((employee) => [employee.id, employee])
    ).values(),
  ];
  const firstDay =
    mode === "day" ? startOfDay(viewDate) : startOfWeek(viewDate, { weekStartsOn: 1 });
  const days = Array.from({ length: mode === "day" ? 1 : 7 }, (_, index) =>
    addDays(firstDay, index)
  );
  const visibleEnd = addDays(firstDay, days.length);
  const selectedStart = fromZonedTime(isAllDay ? startOfDay(start) : start, timezone);
  const selectedEnd = isAllDay
    ? fromZonedTime(end > start ? startOfDay(end) : addDays(startOfDay(start), 1), timezone)
    : fromZonedTime(end, timezone);
  const duration = isAllDay
    ? 60
    : differenceInMinutes(fromZonedTime(end, timezone), fromZonedTime(start, timezone));
  const filter = getBusyFilter(
    employees.map((employee) => employee.id),
    firstDay,
    visibleEnd,
    timezone
  );
  const availability = useEmployeesBusy(filter);
  const busyByEmployee = new Map(availability.data?.map((person) => [person.employee_id, person]));
  const suggestionTarget = set(viewDate, {
    hours: start.getHours(),
    minutes: start.getMinutes(),
    seconds: 0,
    milliseconds: 0,
  });
  const suggestions =
    !availability.isFetching && !availability.isError
      ? suggestAvailableTimes(suggestionTarget, timezone, duration, availability.data ?? [])
      : [];
  const lanes = days.flatMap((day) =>
    employees.map((employee) => ({
      day,
      employee,
      key: `${format(day, "yyyy-MM-dd")}-${employee.id}`,
    }))
  );
  const timeLabel = (instant: Date) => format(toZonedTime(instant, timezone), "HH:mm");
  const dateLabel = (day: Date) => format(day, "d MMM", { locale });
  const chooseInstant = (instant: Date) => {
    if (duration <= 0) return;
    onSelect(toZonedTime(instant, timezone), toZonedTime(addMinutes(instant, duration), timezone));
  };
  const chooseMinute = (day: Date, minutes: number) =>
    chooseInstant(
      fromZonedTime(
        set(day, {
          hours: Math.floor(minutes / 60),
          minutes: minutes % 60,
          seconds: 0,
          milliseconds: 0,
        }),
        timezone
      )
    );
  const gridColumns = `64px repeat(${Math.max(1, lanes.length)}, minmax(0, 1fr))`;
  const currentDate = toZonedTime(new Date(), timezone);

  const selectedHour = start.getHours();
  const gmtLabel = getGmtLabel(
    fromZonedTime(set(viewDate, { hours: 12, minutes: 0, seconds: 0, milliseconds: 0 }), timezone),
    timezone
  );
  const timezoneTooltip = t("calendar:availability_meeting_timezone", {
    timezone,
    offset: gmtLabel,
  });

  useLayoutEffect(() => {
    if (scrollRef.current)
      scrollRef.current.scrollTop = Math.max(0, (selectedHour - 1) * hourHeight);
  }, [startTimestamp, selectedHour, timezone, mode, availability.isFetching]);

  return (
    <section
      className="flex h-full min-h-0 min-w-0 flex-col bg-light"
      aria-label={t("calendar:availability_title")}
    >
      <Stack gap="sm" className="shrink-0 border-b border-dark/10 p-4">
        <Stack
          direction="horizontal"
          gap="sm"
          items="center"
          justify="between"
          className="flex-wrap"
        >
          <Stack direction="horizontal" gap="sm" items="center" className="flex-wrap">
            <ButtonIcon
              size="sm"
              variant="plain"
              aria-label={t("calendar:availability_close")}
              icon={<FontAwesomeIcon icon="fa-light fa-xmark" />}
              onClick={onClose}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setViewDate(startOfDay(currentDate))}
            >
              {t("common:today")}
            </Button>
            <Stack gap={"xs"} direction="horizontal" items="center">
              <ButtonIcon
                size="sm"
                variant="plain"
                aria-label={t("common:previous")}
                icon={<FontAwesomeIcon icon="fa-light fa-chevron-left" />}
                onClick={() => setViewDate(addDays(viewDate, mode === "day" ? -1 : -7))}
              />
              <Text className="font-semibold">
                {mode === "day"
                  ? format(viewDate, "d MMMM yyyy", { locale })
                  : `${dateLabel(firstDay)} – ${dateLabel(days[6])}, ${format(days[6], "yyyy")}`}
              </Text>

              <ButtonIcon
                variant="plain"
                size="sm"
                aria-label={t("common:next")}
                icon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
                onClick={() => setViewDate(addDays(viewDate, mode === "day" ? 1 : 7))}
              />
            </Stack>
          </Stack>
          <Stack direction="horizontal" gap="xs" className="rounded-sm bg-dark/5 p-1">
            <Button
              size="sm"
              variant={mode === "day" ? "primary" : "plain"}
              aria-pressed={mode === "day"}
              onClick={() => setMode("day")}
            >
              {t("calendar:availability_day")}
            </Button>
            <Button
              size="sm"
              variant={mode === "week" ? "primary" : "plain"}
              aria-pressed={mode === "week"}
              onClick={() => setMode("week")}
            >
              {t("common:week")}
            </Button>
          </Stack>
        </Stack>
        {isEditing && (
          <Text size="sm" className="text-secondary">
            {t("calendar:availability_edit_note")}
          </Text>
        )}
      </Stack>
      {!employees.length ? (
        <div className="flex grow items-center justify-center p-8 text-secondary">
          {t("calendar:availability_add_employees")}
        </div>
      ) : availability.isError ? (
        <Stack gap="sm" className="grow items-center justify-center p-8">
          <Text role="alert" className="text-danger">
            {t("calendar:availability_error")}
          </Text>
          <Button variant="secondary" onClick={() => availability.refetch()}>
            {t("calendar:availability_retry")}
          </Button>
        </Stack>
      ) : availability.isFetching ? (
        <Stack
          gap="md"
          className="grow overflow-hidden p-5"
          role="status"
          aria-label={t("common:loading")}
        >
          <Skeleton className="h-16 w-full rounded-sm" />
          {Array.from({ length: 7 }, (_, index) => (
            <Skeleton key={index} className="h-12 w-full rounded-sm" />
          ))}
        </Stack>
      ) : (
        <div
          ref={scrollRef}
          className="min-h-0 grow overflow-auto overscroll-contain"
          tabIndex={0}
          aria-label={t("calendar:availability_title")}
        >
          <div
            style={{
              minWidth: Math.max(
                mode === "week" ? 760 : 400,
                lanes.length * (mode === "week" ? 90 : 160) + 64
              ),
            }}
          >
            <div className="sticky top-0 z-20 bg-light shadow-xs">
              {mode === "week" && (
                <div
                  className="grid border-b border-dark/10"
                  style={{ gridTemplateColumns: gridColumns }}
                >
                  <div className="sticky left-0 z-30 bg-light" />
                  {days.map((day) => (
                    <div
                      key={day.toISOString()}
                      className="border-r border-dark/10 px-2 py-2 text-center text-sm font-semibold"
                      style={{ gridColumn: `span ${employees.length}` }}
                    >
                      {format(day, "EEE, d MMM", { locale })}
                    </div>
                  ))}
                </div>
              )}
              <div
                className="grid border-b border-dark/10"
                style={{ gridTemplateColumns: gridColumns }}
              >
                <div className="sticky left-0 z-30 flex items-end justify-center bg-light pb-2">
                  <Tooltip>
                    <TooltipTrigger>
                      <button
                        type="button"
                        className="cursor-help whitespace-nowrap text-xs text-secondary"
                        aria-label={timezoneTooltip}
                      >
                        {gmtLabel}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-80">{timezoneTooltip}</TooltipContent>
                  </Tooltip>
                </div>
                {lanes.map(({ key, employee }) => (
                  <Stack
                    key={key}
                    gap="xs"
                    items="center"
                    className="min-w-0 border-r border-dark/10 px-2 py-2"
                  >
                    {mode === "day" && <EmployeeAvatar employee={employee} size="sm" />}
                    <div
                      className="w-full truncate text-center text-xs"
                      title={renderEmployeeName(employee)}
                    >
                      {renderEmployeeName(employee)}
                    </div>
                  </Stack>
                ))}
              </div>
            </div>
            <div className="grid" style={{ gridTemplateColumns: gridColumns }}>
              <div
                className="sticky left-0 z-10 bg-light text-xs text-secondary"
                style={{ height: hourHeight * 24 }}
              >
                {hours.map((hour) => (
                  <span key={hour} className="absolute right-2" style={{ top: hour * hourHeight }}>
                    {String(hour).padStart(2, "0")}:00
                  </span>
                ))}
              </div>
              {lanes.map(({ key, day, employee }) => {
                const intervals = busyByEmployee.get(employee.id)?.intervals ?? [];
                const selection = getTimelineBlock(
                  { datetime_start: selectedStart, datetime_end: selectedEnd },
                  day,
                  timezone
                );
                return (
                  <button
                    key={key}
                    type="button"
                    className="relative border-r border-dark/10 text-left focus-visible:outline-2 focus-visible:outline-brand-dark"
                    style={{ height: hourHeight * 24 }}
                    aria-label={`${t("calendar:availability_select_for", { name: renderEmployeeName(employee) })} — ${dateLabel(day)}`}
                    onClick={(event) => {
                      const rect = event.currentTarget.getBoundingClientRect();
                      const minutes =
                        event.detail === 0
                          ? start.getHours() * 60 + start.getMinutes()
                          : Math.max(
                              0,
                              Math.min(
                                1425,
                                Math.round(((event.clientY - rect.top) / hourHeight) * 4) * 15
                              )
                            );
                      chooseMinute(day, minutes);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
                      event.preventDefault();
                      chooseMinute(
                        day,
                        Math.max(
                          0,
                          Math.min(
                            1425,
                            start.getHours() * 60 +
                              start.getMinutes() +
                              (event.key === "ArrowDown" ? 15 : -15)
                          )
                        )
                      );
                    }}
                  >
                    {hours.map((hour) => (
                      <span
                        aria-hidden="true"
                        key={hour}
                        className="pointer-events-none absolute inset-x-0 border-t border-dark/10"
                        style={{ top: hour * hourHeight }}
                      />
                    ))}
                    {intervals.map((interval, index) => {
                      const block = getTimelineBlock(interval, day, timezone);
                      if (!block) return null;
                      const label = t("calendar:availability_busy_time", {
                        start: timeLabel(new Date(interval.datetime_start)),
                        end: timeLabel(new Date(interval.datetime_end)),
                      });
                      return (
                        <span
                          key={index}
                          className="absolute inset-x-1 rounded-[4px] shadow-[0_0_0_1px_var(--color-light)]"
                          style={{ top: `${block.top}%`, height: `${block.height}%`, minHeight: 3 }}
                          title={label}
                        >
                          <CalendarEventCard
                            durationMinutes={differenceInMinutes(
                              new Date(interval.datetime_end),
                              new Date(interval.datetime_start)
                            )}
                            appearanceClassName="bg-brand-dark/80 text-light"
                            time={
                              differenceInMinutes(
                                new Date(interval.datetime_end),
                                new Date(interval.datetime_start)
                              ) >= 60
                                ? `${timeLabel(new Date(interval.datetime_start))} — ${timeLabel(new Date(interval.datetime_end))}`
                                : timeLabel(new Date(interval.datetime_start))
                            }
                          />
                        </span>
                      );
                    })}
                    {selection && (
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 border bg-brand-dark/5 rounded-sm border-dark/5"
                        style={{
                          top: `${selection.top}%`,
                          height: `${selection.height}%`,
                          minHeight: 3,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {employees.length > 0 && (
        <Stack gap="sm" className="shrink-0 border-t border-dark/10 bg-light px-5 py-4">
          <Text size="sm" className="font-semibold">
            {t("calendar:availability_suggestions")} · {dateLabel(viewDate)}
          </Text>
          <Stack direction="horizontal" gap="sm" className="flex-wrap">
            {availability.isFetching
              ? [0, 1, 2].map((index) => <Skeleton key={index} className="h-9 w-28 rounded-sm" />)
              : suggestions.map((instant) => (
                  <Button
                    size="sm"
                    variant="secondary"
                    key={instant.toISOString()}
                    onClick={() => chooseInstant(instant)}
                  >
                    {timeLabel(instant)}–{timeLabel(addMinutes(instant, duration))}
                  </Button>
                ))}
            {!availability.isFetching && !availability.isError && !suggestions.length && (
              <Text size="sm" className="text-secondary">
                {t("calendar:availability_no_slots")}
              </Text>
            )}
          </Stack>
          {hasGroups && (
            <InformBox
              title={t("calendar:availability_groups_title")}
              description={t("calendar:availability_groups_note")}
            />
          )}
        </Stack>
      )}
    </section>
  );
}
